<template>
  <FormContainer class="form-container">
    <template #title>
      <BasicText
        size="h4"
        weight="bold"
        class="flex-1"
      >
        Prise en charge et facturation
      </BasicText>
      <BasicButton
        @click="toggleDetail"
        icon-name="arrow-up"
        :label="showDetail ? 'Masquer detail' : 'Montrer detail'"
        icon-right
        class="btn-detail"
        :class="showDetail ? 'close' : 'open'"
      />
    </template>
    <template #content>
      <div v-if="showDetail">
        <!-- Desktop -->
        <div class="mobile-none tablet-none desktop flex flex-column flex-gap-12">
          <!-- Table A, B, C -->
          <div class="elements--desktop">
            <!-- Header -->
            <div class="cardLayoutWrapper cardLayoutWrapper--header">
              <div class="elem elem--span-2"></div>
              <div class="elem elem--span-14">
                <BasicText
                  size="body-xl"
                  weight="bold"
                >
                  Numéro Dossier : {{ data.dossier }}
                </BasicText>
              </div>
              <div
                v-for="nf in RegroupementFraisNature"
                :key="nf"
                class="elem elem--span-5"
                style="justify-content: flex-end"
              >
                <span>
                  {{ RegroupementFraisNatureLibelle[nf as RegroupementFraisNature] }}
                </span>
              </div>
            </div>
            <!-- A, B, C -->
            <div
              class="listCardDesktopWrapper"
              v-for="ligne in [data.a, data.b, data.c]"
              :key="ligne.lettre"
            >
              <div class="cardLayoutWrapper">
                <div class="elem elem--span-2">
                  <BasicBadge :label="ligne.lettre"></BasicBadge>
                </div>
                <div class="elem elem--span-14">
                  <span>
                    <strong>{{ ligne.libelle }}</strong>
                  </span>
                </div>
                <div
                  v-for="nf in RegroupementFraisNature"
                  :key="nf"
                  class="elem elem--span-5 elem--right"
                >
                  <span>{{ formatMontant(ligne.frais[nf]) }} €</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Table D, E, F -->
          <div class="elements--desktop">
            <!-- Header -->
            <div class="cardLayoutWrapper cardLayoutWrapper--header">
              <div
                class="elem elem--span-2"
                style="justify-content: flex-start"
              >
                <BasicBadge :label="data.d.lettre"></BasicBadge>
              </div>
              <div class="elem elem--span-34">
                <BasicText
                  size="body-xl"
                  weight="bold"
                >
                  {{ data.d.libelle }}
                </BasicText>
              </div>
              <div class="elem elem--span-5">
                <span>Type</span>
              </div>
              <div class="elem elem--span-6">
                <span>Etat</span>
              </div>
              <div class="elem elem--span-5">
                <span>Référence</span>
              </div>
              <div
                v-for="nf in RegroupementFraisNature"
                :key="nf"
                class="elem elem--span-5"
                style="justify-content: flex-end"
              >
                <span>
                  {{ RegroupementFraisNatureLibelle[nf as RegroupementFraisNature] }}
                </span>
              </div>
            </div>
            <!-- D - Factures-->
            <div
              class="listCardDesktopWrapper"
              v-for="(ligne, index) in data.d.factures"
              :key="index"
            >
              <div class="cardLayoutWrapper flex-gap-2">
                <div class="elem elem--span-5">
                  {{ TypeFacturationLibelle[ligne.type] }}
                </div>

                <div class="elem elem--span-6">
                  <BasicBadge
                    :type="ligne.statutBadge"
                    :label="ligne.statutLibelle"
                  ></BasicBadge>
                </div>
                <div class="elem elem--span-5">
                  {{ ligne.reference }}
                </div>
                <div
                  v-for="nf in RegroupementFraisNature"
                  :key="nf"
                  class="elem elem--span-5 elem--right"
                >
                  <span>{{ formatMontant(ligne.frais[nf]) }} €</span>
                </div>
              </div>
            </div>
            <div v-if="!data.d.factures.length">
              <EmptyDataTable>
                <template #content>
                  <strong>Aucune facture transmise</strong>
                </template>
              </EmptyDataTable>
            </div>

            <!-- E - Total factures-->
            <div class="listCardDesktopWrapper">
              <div class="cardLayoutWrapper">
                <div class="elem elem--span-16 flex-gap-4">
                  <BasicBadge :label="data.e.lettre"></BasicBadge>
                  <span>
                    <strong>{{ data.e.libelle }}</strong>
                  </span>
                </div>
                <div
                  v-for="nf in RegroupementFraisNature"
                  :key="nf"
                  class="elem elem--span-5 elem--right"
                >
                  <span>{{ formatMontant(data.e.frais[nf]) }} €</span>
                </div>
              </div>
            </div>

            <!-- Séparateur -->
            <div class="listCardDesktopWrapper">
              <div class="cardLayoutWrapper"></div>
            </div>

            <!-- F - Reste à facturer -->
            <div class="listCardDesktopWrapper">
              <div class="cardLayoutWrapper">
                <div class="elem elem--span-16 flex-gap-4">
                  <BasicBadge :label="data.f.lettre"></BasicBadge>
                  <span>
                    <strong>{{ data.f.libelle }}</strong>
                  </span>
                </div>
                <div
                  v-for="nf in RegroupementFraisNature"
                  :key="nf"
                  class="elem elem--span-5 elem--right"
                >
                  <span>{{ formatMontant(data.f.frais[nf]) }} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Mobile -->
        <div class="desktop-none mobile flex flex-column flex-gap-12">
          <!-- A, B, C -->
          <div class="elements--desktop">
            <template
              v-for="ligne in [data.a, data.b, data.c]"
              :key="ligne.lettre"
            >
              <!-- Header -->
              <div class="cardLayoutWrapper cardLayoutWrapper--header">
                <!-- Num dossier -->
                <template v-if="ligne.lettre === 'A'">
                  <div class="elem elem--span-20">
                    <BasicText
                      size="body-xl"
                      weight="bold"
                    >
                      Numéro Dossier
                    </BasicText>
                  </div>
                  <div
                    class="elem elem--span-16"
                    style="justify-content: flex-end"
                  >
                    <BasicText
                      size="body-xl"
                      weight="bold"
                    >
                      {{ data.dossier }}
                    </BasicText>
                  </div>
                </template>
                <!-- A, B, C -->
                <div class="elem elem--span-4">
                  <BasicBadge :label="ligne.lettre"></BasicBadge>
                </div>
                <div
                  class="elem elem--span-32"
                  style="justify-content: flex-end"
                >
                  <BasicText
                    size="body-xl"
                    weight="bold"
                  >
                    {{ ligne.libelle }}
                  </BasicText>
                </div>
              </div>
              <!-- Details A, B, C -->
              <div
                class="listCardDesktopWrapper"
                v-for="nf in RegroupementFraisNature"
                :key="nf"
              >
                <div class="cardLayoutWrapper">
                  <div class="elem elem--span-12">
                    <span>{{ nf }}</span>
                  </div>
                  <div class="elem elem--span-24 elem--right">
                    <span>{{ formatMontant(ligne.frais[nf]) }} €</span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- D, E -->
          <div class="elements--desktop">
            <!-- D - Détail factures-->
            <template
              v-for="(facture, index) in data.d.factures"
              :key="facture.reference"
            >
              <!-- Header -->
              <div class="cardLayoutWrapper cardLayoutWrapper--header">
                <template v-if="index === 0">
                  <div
                    class="elem elem--span-4"
                    style="justify-content: flex-start"
                  >
                    <BasicBadge :label="data.d.lettre"></BasicBadge>
                  </div>
                  <div
                    class="elem elem--span-32"
                    style="justify-content: flex-end"
                  >
                    <BasicText
                      size="body-xl"
                      weight="bold"
                    >
                      {{ data.d.libelle }}
                    </BasicText>
                  </div>
                </template>
                <div
                  class="elem elem--span-36 flex-gap-1"
                  style="justify-content: space-between"
                >
                  <span class="flex-1">
                    Type :
                    <strong>{{ TypeFacturationLibelle[facture.type] }}</strong>
                  </span>
                  <BasicBadge
                    :label="facture.statutLibelle"
                    :type="facture.statutBadge"
                    style="min-width: fit-content"
                  ></BasicBadge>
                  <span
                    class="flex-1"
                    style="text-align: end"
                  >
                    Réf :
                    <strong>{{ facture.reference }}</strong>
                  </span>
                </div>
              </div>
              <!-- Detail facture -->
              <div
                class="listCardDesktopWrapper"
                v-for="nf in RegroupementFraisNature"
                :key="nf"
              >
                <div class="cardLayoutWrapper">
                  <div class="elem elem--span-12">
                    <span>{{ nf }}</span>
                  </div>
                  <div class="elem elem--span-24 elem--right">
                    <span>{{ formatMontant(facture.frais[nf]) }} €</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="!data.d.factures.length">
              <div class="cardLayoutWrapper cardLayoutWrapper--header">
                <div
                  class="elem elem--span-4"
                  style="justify-content: flex-start"
                >
                  <BasicBadge :label="data.d.lettre"></BasicBadge>
                </div>
                <div
                  class="elem elem--span-32"
                  style="justify-content: flex-end"
                >
                  <BasicText
                    size="body-xl"
                    weight="bold"
                  >
                    {{ data.d.libelle }}
                  </BasicText>
                </div>
              </div>
              <EmptyDataTable>
                <template #content>
                  <strong>Aucune facture transmise</strong>
                </template>
              </EmptyDataTable>
            </div>

            <!-- E - Total facturé-->
            <!-- Header -->
            <div class="cardLayoutWrapper cardLayoutWrapper--header">
              <div
                class="elem elem--span-4"
                style="justify-content: flex-start"
              >
                <BasicBadge :label="data.e.lettre"></BasicBadge>
              </div>
              <div
                class="elem elem--span-32"
                style="justify-content: flex-end"
              >
                <BasicText
                  size="body-xl"
                  weight="bold"
                >
                  {{ data.e.libelle }}
                </BasicText>
              </div>
            </div>
            <!-- Total factures -->
            <div
              class="listCardDesktopWrapper"
              v-for="nf in RegroupementFraisNature"
              :key="nf"
            >
              <div class="cardLayoutWrapper">
                <div class="elem elem--span-12">
                  <span>{{ nf }}</span>
                </div>
                <div class="elem elem--span-24 elem--right">
                  <span>{{ formatMontant(data.e.frais[nf]) }} €</span>
                </div>
              </div>
            </div>
          </div>

          <!-- F - Reste à facturer -->
          <div class="elements--desktop">
            <!-- Header -->
            <div class="cardLayoutWrapper cardLayoutWrapper--header">
              <div
                class="elem elem--span-4"
                style="justify-content: flex-start"
              >
                <BasicBadge :label="data.f.lettre"></BasicBadge>
              </div>
              <div
                class="elem elem--span-32"
                style="justify-content: flex-end"
              >
                <BasicText
                  size="body-xl"
                  weight="bold"
                >
                  {{ data.f.libelle }}
                </BasicText>
              </div>
            </div>
            <!-- Detail reste à facturé -->
            <div
              class="listCardDesktopWrapper"
              v-for="nf in RegroupementFraisNature"
              :key="nf"
            >
              <div class="cardLayoutWrapper">
                <div class="elem elem--span-12">
                  <span>
                    {{ nf }}
                  </span>
                </div>
                <div class="elem elem--span-24 elem--right">
                  <span>{{ formatMontant(data.f.frais[nf]) }} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="form__bloc"
      >
        <div
          v-for="nf in RegroupementFraisNature"
          :key="nf"
          class="basic-card col-9 col-md-18 col-sm-36"
        >
          <BasicText
            weight="bold"
            :label="RegroupementFraisNatureLibelle[nf as RegroupementFraisNature]"
          />
          <BasicText>{{ formatMontant(data.f.frais[nf as RegroupementFraisNature]) }} €</BasicText>
        </div>
        <BasicAlert alertLabel="Montants restant à facturer" alertType="info" class="col-36"></BasicAlert>
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { useSyntheseReglements } from './useSyntheseReglements'
  import { EmptyDataTable, FormContainer } from '@/features/interface/demande/shared'
  import {
    RegroupementFraisNature,
    RegroupementFraisNatureLibelle,
    TypeFacturationLibelle,
  } from '@/features/api/extranet/dto/Dossier/Synthese'
  import { formatNumber, isNumber } from '@/features/shared/tools'
  import { ref } from 'vue'
  const { data } = useSyntheseReglements()

  const showDetail = ref(false)
  const toggleDetail = () => (showDetail.value = !showDetail.value)

  const formatMontant = (value: number | null | undefined) => {
    if (!isNumber(value)) return '-'
    return formatNumber(value, { decimal: 2, separateur: true })
  }
</script>

<style lang="less" scoped>
  /* Gestion du titre */
  .form-container:deep(.form__title) {
    top: -3px;
    padding-bottom: 18px;
    margin-bottom: -18px;
    .text.h4 {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .btn-detail {
    width: 170px;
    justify-content: flex-start;

    &:deep(.icon-container) {
      transition: transform 0.3s ease-in-out;
    }

    &.open:deep(.icon-container) {
      transform: rotate(0.5turn);
    }
  }

  .device-tablet,
  .device-mobile {
    .btn-detail {
      max-width: fit-content;
      justify-self: flex-start;
      &:deep(.text) {
        display: none;
      }
    }
  }

  /* Affichage des tableaux */
  .cardLayoutWrapper:deep(.elem .badge span) {
    color: @primary-600;
  }

  .dispositif .elements--desktop {
    display: block;
  }

  .elements--desktop .cardLayoutWrapper.cardLayoutWrapper--header:not(:first-child) {
    border-radius: 0 0 0 0;
  }

  .elements--desktop span.text.body-xl {
    font-size: 16px;
  }
</style>
